import { loadUnavailability } from "./utils/unavailability";
import { CLOSED_DAY_TOAST, noSlotMessage } from "./utils/noSlotMessage";
import { toastOnce } from "./utils/toastOnce";
import { useData } from "data/data-provider";
import moment from "moment";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { changeDate } from "utils/changeDate";
import ClearDataButton from "../ClearDataButton";
import DatePickerCustomDay from "../DatePickerCustomDay";

// Types
import {
  DatePickerType,
  RentalCarDatesRangeInputProps,
  TabType,
} from "./types/rentalCarDatesRangeInput.types";

// Utils
import {
  formatDate,
  isOutsideOperationalHoursOn,
  DATE_TIME_COPY,
  isTimeBooked,
  timeSlots,
  TripEnd,
  tripDateRange,
  windowForDate,
} from "@1-now/booking-core";
import {
  getAllWeeksForSelection,
  getMonthsEndDate,
  getSelectedWeeksDateRange,
  getWeekIntervalDays,
  weekContainsUnavailableDates,
} from "./utils/weeksHelper";

import { useDialInteraction } from "./hooks/useDialInteraction";

// Hooks
import { useClickOutside } from "./hooks/useClickOutside";
import { useDateAvailability } from "./hooks/useDateAvailability";
import { useScreenSize } from "./hooks/useScreenSize";

// Components
import DatesView from "./components/DatesView";
import TabSwitcher from "./components/TabSwitcher";
import WeeksView from "./components/WeeksView";
import MonthsView from "./components/MonthsView";
import HoursView from "./HoursView";
import { CalendarIcon, XMarkIcon } from "@heroicons/react/24/outline";
import PickerLayer from "../CarPageB/PickerLayer";
import { useIsSheet } from "../CarPageB/useIsSheet";
import TripTile from "../CarPageB/TripTile";
import { CalendarNote, dayContents } from "../CarPageB/calendarParts";
import { freeHoursLabel, partialKey } from "../CarPageB/availability";

// How this picker writes a time into the "No available time slot" message.
const twelveHour = (time: string) => moment(time, "HH:mm").format("h:mm A");

// What a renter is told when the dates they chose run through days the car is booked (1NOW-11215).
const BOOKED_IN_RANGE = "Your dates include days this car is already booked. Choose dates around them.";
const PART_BOOKED_IN_RANGE = "This car is already booked for part of a day between your dates. Choose dates around it.";

const DateRangeSelection: FC<RentalCarDatesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  selectedCar,
  isMiddle,
  searchBar,
  minDays,
  maxDays,
  bookingDetails,
  onSaveDates,
  look,
}) => {
  const b = look === "b";
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setPartiallyAvailableStartDate,
    setPartiallyAvailableEndDate,
    setPartiallyUnavailableTimes,
    partiallyUnavailableTimes,
    setLongTermRentalDuration,
    setLongTermRentalNature,
    long_term_rental_nature,
    setIsHourlyRental,
    setIsLongTermRental,
    pickupLocation,
    startTime,
    endTime,
    setStartTime,
    setEndTime,
    s_Time,
    e_Time,
    activeTab,
    setSelectedWeeks,
    selectedWeeks,
    monthEndDate,
    setMonthEndDate,
    monthStartDate,
    setMonthStartDate,
    selectedMonth,
    setActiveTab,
    setSelectedMonth,
    weeksViewMonth,
    ourCompany,
  } = useData();

  const dialRef = useRef<HTMLDivElement>(null);

  const [fullyUnavailableDates, setFullyUnavailableDates] = useState<Date[]>(
    [],
  );
  const [partialUnavailability, setPartialUnavailability] = useState<{
    [key: string]: { from: string; to: string }[];
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState<Date | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  // Design B: the limited-hours day tapped for its free hours (phones have no hover).
  const [tapped, setTapped] = useState<Date | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { isDragging, handleMouseDown, handleTouchStart } = useDialInteraction(
    dialRef,
    setSelectedMonth,
  );

  const [showDatePicker, setShowDatePicker] = useState<DatePickerType>(null);
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);

  const { isLargeScreen } = useScreenSize();
  const monthsToShow = isLargeScreen ? 2 : 1;

  const { isDateSelectable, isRangeAvailable, isPartialOverlapValid } =
    useDateAvailability(fullyUnavailableDates, partialUnavailability);

  // Only Design A on a phone opens the panel under its field. Everywhere else it is PickerLayer's dialog (Design B's
  // phones: its sheet), which closes from its own backdrop, close button and Escape, not from a click outside.
  const sheet = useIsSheet();
  const underField = !b && sheet;
  useClickOutside(panelRef, isPanelOpen && !showDatePicker && underField, () => setIsPanelOpen(false));

  // Initialize temp dates when panel opens
  useEffect(() => {
    if (isPanelOpen) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
    }
  }, [isPanelOpen]);

  // Update end date when selected month changes. How long a month is depends on the times as well (1NOW-11212).
  useEffect(() => {
    setMonthEndDate(
      getMonthsEndDate(
        monthStartDate,
        selectedMonth,
        startTime && startTime.trim() !== "" ? startTime : s_Time,
        endTime && endTime.trim() !== "" ? endTime : e_Time,
      ),
    );
  }, [selectedMonth, monthStartDate, startTime, endTime]);

  // Calculate months from date range
  const calculateMonthsFromDates = useCallback((start: Date, end: Date) => {
    const days = moment(end).diff(moment(start), "days") + 1;
    const months = Math.round(days / 30);
    return Math.max(1, Math.min(12, months));
  }, []);

  // Open date picker modal
  const openDatePickerModal = (type: DatePickerType) => {
    setShowDatePicker(type);
    setTempSelectedDate(type === "start" ? monthStartDate : monthEndDate);
  };

  const handleModalDateChange = (date: Date | null) => {
    if (date) setTempSelectedDate(date);
  };

  // Save the selected date from modal
  const handleModalSave = () => {
    if (!tempSelectedDate || !showDatePicker) return;
    if (!firstOpenSlot(tempSelectedDate, showDatePicker)) {
      toastOnce(noTimeMessage(showDatePicker === "start" ? "pick-up" : "return", tempSelectedDate));
      return;
    }

    if (showDatePicker === "start") {
      setMonthStartDate(tempSelectedDate);
      const months = calculateMonthsFromDates(tempSelectedDate, monthEndDate);
      setSelectedMonth(months);
    } else if (showDatePicker === "end") {
      setMonthEndDate(tempSelectedDate);
      const months = calculateMonthsFromDates(monthStartDate, tempSelectedDate);
      setSelectedMonth(months);
    }
    setShowDatePicker(null);
    setTempSelectedDate(null);
  };

  // Close modal without saving
  const handleModalClose = () => {
    setShowDatePicker(null);
    setTempSelectedDate(null);
  };

  const fetchUnavailableDates = (startDate: Date, endDate: Date) =>
    loadUnavailability(
      startDate,
      endDate,
      {
        fleetId: selectedCar?.id,
        locationId: pickupLocation?.id,
        bookingToExclude: bookingDetails?.id,
      },
      {
        setLoading: setIsLoading,
        setFullyUnavailable: setFullyUnavailableDates,
        setPartialUnavailability,
        setPartiallyUnavailableTimes,
      },
    );

  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const startMoment = moment(dates[0]);
    const endMoment = moment(dates[1]);

    // A day the car or the branch leaves no time on can't start or end a trip. Say so now, not on Save
    // (1NOW-11215): the day is not taken, and a start already chosen stays.
    if (dates[0] && !firstOpenSlot(dates[0], "start")) {
      toastOnce(noTimeMessage("pick-up", dates[0]));
      return;
    }
    if (dates[1] && !firstOpenSlot(dates[1], "end")) {
      toastOnce(noTimeMessage("return", dates[1]));
      setTempStartDate(dates[0]);
      setTempEndDate(null);
      return;
    }

    // A range with booked days in it is not taken either; the start stays, so only an end has to be chosen again.
    if (!isRangeAvailable(startMoment, endMoment)) {
      setTempStartDate(dates[0]);
      setTempEndDate(null);
      toastOnce(BOOKED_IN_RANGE);
      return;
    }

    if (!isPartialOverlapValid(startMoment, endMoment)) {
      setTempStartDate(dates[0]);
      setTempEndDate(null);
      toastOnce(PART_BOOKED_IN_RANGE);
      return;
    }
    // Update temporary state only
    setTempStartDate(dates[0]);
    setTempEndDate(dates[1]);

    const today = new Date();
    const visibleEndDate = new Date(today);
    visibleEndDate.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, visibleEndDate);
  };

  useEffect(() => {
    // Drop the previous vehicle's blocked hours before asking for this one's.
    // This context is app-level and nothing else ever clears it, so without this
    // the window between mounting a second vehicle and its fetch resolving is
    // answered with the first vehicle's availability — and since a car with no
    // blocks resolves to `{}`, nothing would move the time back afterwards.
    if (setPartiallyUnavailableTimes) setPartiallyUnavailableTimes({});
    const today = new Date();
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, threeMonthsLater);
  }, [selectedCar?.id, monthsToShow, pickupLocation]);

  // Fetch unavailable dates when weeks view month changes
  useEffect(() => {
    if (activeTab === 'weeks' && weeksViewMonth) {
      const monthStart = moment(weeksViewMonth).startOf('month').toDate();
      const monthEnd = moment(weeksViewMonth).endOf('month').toDate();
      fetchUnavailableDates(monthStart, monthEnd);
    }
  }, [weeksViewMonth, activeTab]);

  // A change of the times moves the saved weeks or months with it: how long a week or a month runs depends on
  // them (the drop-off later in the day than the pick-up takes a day off), and the dates carry the times the
  // Booking overview shows. The time field leaves weeks and months to this (RentalCarTimesRangeInput).
  useEffect(() => {
    if (!startDate || !endDate) return;
    const currentStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
    const currentEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
    if (!currentStartTime || !currentEndTime) return;

    if (activeTab === 'weeks' && selectedWeeks && selectedWeeks.length > 0) {
      const range = getSelectedWeeksDateRange(selectedWeeks, [], currentStartTime, currentEndTime);
      if (range) {
        setStartDate(range.start);
        setEndDate(range.end);
      }
    } else if (activeTab === 'months' && long_term_rental_nature === 'monthly') {
      // Only a trip saved as months: browsing the tab over a daily trip changes nothing.
      const months = calculateMonthsFromDates(startDate, endDate);
      changeDate(
        [startDate, getMonthsEndDate(startDate, months, currentStartTime, currentEndTime)],
        setStartDate,
        setEndDate,
        currentStartTime,
        currentEndTime,
      );
    } else if (activeTab === 'weeks' || activeTab === 'months') {
      // Dates left over from one of these tabs, nothing chosen in it now: the clock still follows the form.
      changeDate([startDate, endDate], setStartDate, setEndDate, currentStartTime, currentEndTime);
    }
  }, [startTime, endTime]); 

  useEffect(() => {
    if (!startDate || !endDate || Object.keys(partialUnavailability).length === 0) return;
    
    const currentStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
    const currentEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;

    const startTimeBlocked = isTimeBlocked(startDate, currentStartTime, "start");
    const endTimeBlocked = isTimeBlocked(endDate, currentEndTime, "end");
    
    if (startTimeBlocked || endTimeBlocked) {
      const opStartDefault = pickupLocation?.operational_start_time ?? s_Time;
      const opEndDefault = pickupLocation?.operational_end_time ?? e_Time;
      const validStartTime = startTimeBlocked ? getAvailableTime(startDate, opStartDefault, "start") : currentStartTime;
      const validEndTime = endTimeBlocked ? getAvailableTime(endDate, opEndDefault, "end") : currentEndTime;

      if (validStartTime === null || validEndTime === null) {
        toastOnce(
          noSlotMessage(
            pickupLocation,
            validStartTime === null ? startDate : endDate,
            twelveHour,
          )
        );
        setStartDate(null);
        setEndDate(null);
        return;
      }

      // Update the date objects with the new times
      if (validStartTime !== currentStartTime || validEndTime !== currentEndTime) {
        changeDate([startDate, endDate], setStartDate, setEndDate, validStartTime, validEndTime);
        // Also update time state
        if (validStartTime !== startTime) {
          setStartTime(validStartTime);
        }
        if (validEndTime !== endTime) {
          setEndTime(validEndTime);
        }
      }
    }
  }, [partialUnavailability, startDate, endDate]);

  // Helper function to check if a time is blocked
  const isTimeBlocked = (
    date: Date | null | undefined,
    time: string,
    end: TripEnd,
  ): boolean => {
    if (!date) return false;
    // Use local partialUnavailability state for immediate accuracy
    const unavailabilityData = partialUnavailability || partiallyUnavailableTimes || {};
    const dateKey = moment(date).format("YYYY-MM-DD");
    // Which edge of a booked window is free depends on the end of the trip
    // (isTimeBooked): a pick-up may start as a booking ends, a drop-off may
    // land as the next one begins.
    return isTimeBooked(time, unavailabilityData[dateKey], end);
  };

  // Helper function to find next available time if default is unavailable.
  // Returns null when every slot within operational hours is also booking-blocked.
  const getAvailableTime = (
    date: Date | null | undefined,
    defaultTime: string,
    end: TripEnd,
  ): string | null => {
    if (!date) return defaultTime;

    const opStart = pickupLocation?.operational_start_time ?? null;
    const opEnd = pickupLocation?.operational_end_time ?? null;

    const isOutsideOp = (t: string): boolean =>
      isOutsideOperationalHoursOn(t, pickupLocation, date);

    // If default time is available and within operational hours, use it
    if (!isTimeBlocked(date, defaultTime, end) && !isOutsideOp(defaultTime)) {
      return defaultTime;
    }

    // The first slot within operational hours that is free, or null when there is none
    return firstOpenSlot(date, end);
  };

  // What a renter is told about a day that has no pick-up (or return) time: that the branch is shut, or which day.
  const noTimeMessage = (kind: "pick-up" | "return", date: Date) =>
    windowForDate(pickupLocation, date) === null
      ? CLOSED_DAY_TOAST
      : `No ${kind} time is free on ${moment(date).format("ddd, MMM D")}. Choose another day.`;

  // The first slot of the day the branch is open and the car is free, for a pick-up or for a drop-off.
  const firstOpenSlot = (date: Date, end: TripEnd): string | null =>
    timeSlots.find(
      (time) => !isTimeBlocked(date, time, end) && !isOutsideOperationalHoursOn(time, pickupLocation, date),
    ) ?? null;

  // A day that is neither a pick-up nor a drop-off day: the car is out for what the branch leaves of it, or the
  // branch is shut. It can't be chosen (1NOW-11215). The calendar asks for every day on every render, so each day is
  // worked out once until the availability or the branch changes.
  const noTimesByDay = useMemo(() => new Map<string, boolean>(), [partialUnavailability, pickupLocation]);
  const hasNoTimes = (date: Date) => {
    const key = formatDate(date);
    let none = noTimesByDay.get(key);
    if (none === undefined) {
      none = !firstOpenSlot(date, "start") && !firstOpenSlot(date, "end");
      noTimesByDay.set(key, none);
    }
    return none;
  };
  const isDayChoosable = (date: Date) => isDateSelectable(date) && !hasNoTimes(date);

  const handleMonthChange = (date: Date) => {
    setCalendarViewDate(date);
    const viewStartDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const viewEndDate = new Date(
      date.getFullYear(),
      date.getMonth() + monthsToShow,
      0,
    );
    fetchUnavailableDates(viewStartDate, viewEndDate);
  };

  const renderDayContents = (day: number, date?: Date) => {
    if (!date) return day;
    // Design B: this car's availability as a plain number, a strike or a small dot.
    if (b) return dayContents(fullyUnavailableDates, partialUnavailability, ourCompany, setTapped, hasNoTimes)(day, date);
    const dateStr = formatDate(date);
    const isPartial = !!partialUnavailability[dateStr];

    return (
      <DatePickerCustomDay
        dayOfMonth={day}
        date={date}
        unavailableDates={fullyUnavailableDates}
        partiallyUnavailable={isPartial}
        partialTimes={partialUnavailability[dateStr]}
        noTimes={hasNoTimes(date)}
      />
    );
  };

  useEffect(() => {
    if (!b && isPanelOpen && window.innerWidth < 768) {
      setTimeout(() => {
        if (panelRef.current) {
          const topPosition = panelRef.current.getBoundingClientRect().top;
          const offset =
            window.pageYOffset || document.documentElement.scrollTop;
          const targetScrollPosition = topPosition + offset - 120;

          window.scrollTo({
            top: targetScrollPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [isPanelOpen]);

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "dates":
        return (
          <DatesView
            look={look}
            startDate={tempStartDate}
            endDate={tempEndDate}
            onChangeDate={onChangeDate}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            isDateSelectable={isDayChoosable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
          />
        );
      case "hours":
        return (
          <HoursView
            look={look}
            isDateSelectable={isDayChoosable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
            calendarViewDate={calendarViewDate}
          />
        );
      case "weeks":
        return (
          <WeeksView
            look={look}
            unavailableDates={fullyUnavailableDates}
            partialUnavailability={partialUnavailability}
          />
        );
      case "months":
        return (
          <MonthsView
            look={look}
            dialRef={dialRef}
            isDragging={isDragging}
            handleMouseDown={handleMouseDown}
            handleTouchStart={handleTouchStart}
            openDatePickerModal={openDatePickerModal}
            showDatePicker={showDatePicker}
            tempSelectedDate={tempSelectedDate}
            handleModalDateChange={handleModalDateChange}
            handleModalSave={handleModalSave}
            handleModalClose={handleModalClose}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            startDate={startDate}
            isDateSelectable={isDayChoosable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
            isLoading={isLoading}
          />
        );
      default:
        return (
          <DatesView
            look={look}
            startDate={tempStartDate}
            endDate={tempEndDate}
            onChangeDate={onChangeDate}
            monthsToShow={monthsToShow}
            calendarViewDate={calendarViewDate}
            isDateSelectable={isDayChoosable}
            renderDayContents={renderDayContents}
            handleMonthChange={handleMonthChange}
          />
        );
    }
  };

  // The times a trip saved from here carries, from the day it starts and the day it ends. They are the form's own
  // times unless the car is out or the branch is shut then, and then the nearest free slot: so the form, the dates
  // and the Booking overview (it reads the clock of the dates) show the same ones (1NOW-11212). Null, with the
  // reason shown, when a day has none.
  const timesForTrip = (start: Date, end: Date) => {
    const formStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
    const formEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;

    // On a first selection the times are always checked; after that only when the car is out then.
    const isFirstSelection = !startDate && !endDate;
    const validStartTime =
      isFirstSelection || isTimeBlocked(start, formStartTime, "start")
        ? getAvailableTime(start, formStartTime, "start")
        : formStartTime;
    const validEndTime =
      isFirstSelection || isTimeBlocked(end, formEndTime, "end")
        ? getAvailableTime(end, formEndTime, "end")
        : formEndTime;

    if (validStartTime === null || validEndTime === null) {
      toastOnce(noSlotMessage(pickupLocation, validStartTime === null ? start : end, twelveHour));
      return null;
    }
    return { start: validStartTime, end: validEndTime };
  };

  // Write a trip: the dates with their times, and the same times in the form.
  const saveTrip = (start: Date, end: Date, times: { start: string; end: string }) => {
    changeDate([start, end], setStartDate, setEndDate, times.start, times.end);
    if (times.start !== startTime) setStartTime(times.start);
    if (times.end !== endTime) setEndTime(times.end);
  };

  // Handle save for months. True when the trip was saved.
  const handleMonthSave = (): boolean => {
    const startMoment = moment(monthStartDate);
    const endMoment = moment(monthEndDate);

    if (!isRangeAvailable(startMoment, endMoment)) {
      toastOnce(BOOKED_IN_RANGE);
      return false;
    }
    const times = timesForTrip(monthStartDate, monthEndDate);
    if (!times) return false;

    saveTrip(monthStartDate, monthEndDate, times);
    // Calculate total months (inclusive):
    const totalDays = endMoment.diff(startMoment, "days") + 1;
    const totalMonths = Math.round(totalDays / 30);
    setLongTermRentalDuration(totalMonths);
    return true;
  };

  const handleWeeksSave = (): boolean => {
    const formStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
    const formEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;
    const allWeeks = getAllWeeksForSelection(
      selectedWeeks,
      fullyUnavailableDates,
      getWeekIntervalDays(formStartTime, formEndTime),
    );
    const range = getSelectedWeeksDateRange(selectedWeeks, allWeeks, formStartTime, formEndTime);
    if (!range) {
      toastOnce("Please select at least one week.");
      return false;
    }

    // Check if any selected week contains unavailable dates
    const sortedWeeks = [...selectedWeeks].sort();
    const firstWeekKey = sortedWeeks[0];

    const weeksWithUnavailability = allWeeks.filter((week) => {
      const keyDate = week.isCurrentWeek ? week.effectiveStart : week.start;
      const key = moment(keyDate).format("YYYY-MM-DD");
      const isFirstWeek = key === firstWeekKey;

      return (
        selectedWeeks.includes(key) &&
        weekContainsUnavailableDates(
          week,
          fullyUnavailableDates,
          partialUnavailability,
          isFirstWeek, // Allow partial availability only for first week
        )
      );
    });

    if (weeksWithUnavailability.length > 0) {
      toastOnce("One or more selected weeks contain unavailable dates.");
      return false;
    }

    if (!isRangeAvailable(moment(range.start), moment(range.end))) {
      toastOnce(BOOKED_IN_RANGE);
      return false;
    }
    const times = timesForTrip(range.start, range.end);
    if (!times) return false;

    saveTrip(range.start, range.end, times);
    setLongTermRentalDuration(selectedWeeks.length);
    return true;
  };

  const handleHourlySave = (): boolean => {
    // HoursView writes directly to startDate/endDate/startTime/endTime.
    // We just validate the resulting range and flip the is_hourly_rental flag.
    if (!startDate || !endDate || !startTime || !endTime) {
      toastOnce("Please pick a pick-up and drop-off date and time.");
      return false;
    }
    const pickup = moment(
      `${moment(startDate).format("YYYY-MM-DD")} ${startTime}`,
      "YYYY-MM-DD HH:mm",
    );
    const dropoff = moment(
      `${moment(endDate).format("YYYY-MM-DD")} ${endTime}`,
      "YYYY-MM-DD HH:mm",
    );
    if (dropoff.diff(pickup, "hours") < 1) {
      toastOnce("Drop-off must be at least 1 hour after pick-up.");
      return false;
    }
    setIsHourlyRental(true);
    setIsLongTermRental(false);
    setLongTermRentalDuration(0);
    return true;
  };

  // The dates tab. True when the trip was saved.
  const handleDatesSave = (): boolean => {
    if (!tempStartDate || !tempEndDate) {
      // Nothing to save: say so and keep the picker open, it did not save.
      toastOnce("Choose your pick-up and drop-off dates, then save.");
      return false;
    }
    const times = timesForTrip(tempStartDate, tempEndDate);
    if (!times) return false;

    saveTrip(tempStartDate, tempEndDate, times);
    const totalDays = moment(tempEndDate).diff(moment(tempStartDate), "days") + 1;
    if (totalDays) {
      setLongTermRentalDuration(totalDays);
    }
    return true;
  };

  const handleSave = () => {
    // A save that did not happen leaves the picker open with its reason shown, and changes nothing
    // (1NOW-11215). Hourly is a separate booking mode: a day-based tab turns the flag off, once it saved.
    switch (activeTab) {
      case "hours":
        if (!handleHourlySave()) return;
        setSelectedMonth(0);
        setSelectedWeeks([]);
        setMonthStartDate(new Date());
        setMonthEndDate(moment().add(29, "days").toDate());
        break;
      case "weeks":
        if (!handleWeeksSave()) return;
        setIsHourlyRental(false);
        setLongTermRentalNature("weekly");
        // 1NOW-1095: explicitly flip the LTR flag back on. handleHourlySave
        // clears it but neither weeks nor months previously
        // restored it, so a user who tried Hours and then switched back
        // to Weeks would send `is_long_term_rental: false` in the payload.
        // That made `/long-term-weekly-quote/` 400 → FE fell back to
        // displaying cycle 1's invoice for every week (the all-same-value
        // symptom QA reported on storefront repro).
        setIsLongTermRental(true);
        setSelectedMonth(1);
        setMonthStartDate(new Date());
        setMonthEndDate(moment().add(29, "days").toDate());
        break;
      case "months":
        if (!handleMonthSave()) return;
        setIsHourlyRental(false);
        setLongTermRentalNature("monthly");
        setIsLongTermRental(true);
        setSelectedWeeks([]);
        break;
      case "dates":
      default:
        if (!handleDatesSave()) return;
        setIsHourlyRental(false);
        setLongTermRentalNature("daily");
        setSelectedWeeks([]);
        setSelectedMonth(1);
        setMonthStartDate(new Date());
        setMonthEndDate(moment().add(29, "days").toDate());
        break;
    }
    setIsPanelOpen(false);

    // Trigger invoice update if callback is provided
    if (onSaveDates) {
      onSaveDates();
    }
  };

  const clearAll = () => {
    setStartDate(null);
    setEndDate(null);
    setTempStartDate(null);
    setTempEndDate(null);
    setPartiallyAvailableStartDate(null);
    setPartiallyAvailableEndDate(null);
    setSelectedWeeks([]);
    setSelectedMonth(0);
    setActiveTab("dates");
    setMonthStartDate(new Date());
    setMonthEndDate(moment().add(29, "days").toDate());
  };

  if (b) {
    const calendarTab = activeTab === "dates" || activeTab === "hours";
    const tappedHours = tapped ? freeHoursLabel(partialUnavailability[partialKey(tapped)], ourCompany) : null;
    return (
      <div ref={wrapperRef} className="relative" data-field="dates-range">
        <TripTile
          layout="field"
          icon={<CalendarIcon className="h-5 w-5" />}
          label={DATE_TIME_COPY.dateRange}
          value={startDate && endDate ? tripDateRange(startDate, endDate) : DATE_TIME_COPY.datePlaceholder}
          empty={!startDate}
          active={isPanelOpen}
          onClick={() => setIsPanelOpen((open) => !open)}
        />
        <PickerLayer
          open={isPanelOpen}
          onClose={() => {
            setIsPanelOpen(false);
            setTapped(null);
          }}
          title="Select dates"
          wrapperRef={wrapperRef}
          desktopMode="modal"
          name="dates"
          footer={
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                {startDate && (
                  <button type="button" onClick={clearAll} className="text-sm font-medium underline">
                    Clear dates
                  </button>
                )}
                {selectedWeeks.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedWeeks([]);
                    }}
                    className="text-sm font-medium underline"
                  >
                    Clear selection
                  </button>
                )}
              </div>
              <button
                type="button"
                className="h-11 shrink-0 rounded-lg bg-primary-brand px-6 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-700"
                onClick={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                Save dates
              </button>
            </div>
          }
        >
          <div ref={panelRef} data-testid="cpb-range-panel" aria-busy={isLoading}>
            <TabSwitcher look="b" />
            <div className={isLoading ? "pointer-events-none opacity-60" : ""}>{renderTabContent()}</div>
            {calendarTab && <CalendarNote tapped={tapped} hours={tappedHours} />}
          </div>
        </PickerLayer>
      </div>
    );
  }

  // Design A's tabs, calendar and Save. From md up they open in the same centred dialog as Design B's (PickerLayer),
  // with Save pinned under the calendar; on a phone, under the field as before. Under the field on a desktop, the
  // panel of a short page (a car with no extras or description, the times not shown until there are dates) ran over
  // the footer and past the end of #root, which is overflow: hidden, so its foot and Save could not be reached
  // (1NOW-11220).
  const panelContent = (
    <>
      <TabSwitcher />

      {/* The calendar stays where it is in the tree while availability loads. Drawn in a different place, it was
          mounted afresh after every click, opened on the start's month and moved the months under the cursor: a
          second click in the right-hand month landed on the month after (1NOW-11220). */}
      <div className={isLoading ? "pointer-events-none opacity-60 select-none" : undefined}>
        <div className="relative">{renderTabContent()}</div>
        {isLoading && (
          <>
            <div className="absolute left-0 bottom-0 w-full px-8">
              <div className="h-1 bg-gray-100 rounded-full overflow-hidden relative">
                <div className="bar-loader h-full bg-blue-400 rounded-full absolute"></div>
              </div>
            </div>
            <div className="absolute inset-0 bg-card-color opacity-0 pointer-events-auto" />
          </>
        )}
      </div>
    </>
  );
  const saveRow = (
    <div className={`flex justify-between items-center align-center${underField ? " mt-6" : ""}`}>
      <div>
        {selectedWeeks.length > 0 && (
          <button
            onClick={(e) => {
              e.preventDefault();
              setSelectedWeeks([]);
            }}
            className=" w-[120px] text-red-600 hover:text-red-700 hover:bg-red-100 bg-red-50 font-semibold rounded-lg py-3 transition-all space-x-2"
          >
            <span>Clear Selection</span>
          </button>
        )}
      </div>
      <div>
        <button
          className=" w-[120px] bg-primary-brand hover:bg-primary-700 text-on-primary font-semibold rounded-lg py-3 transition-all space-x-2"
          onClick={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <span>Save Dates</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div ref={wrapperRef} className={`RentalCarDatesRangeInput relative flex ${className}`}>
        <div className={`flex-1 flex items-center focus:outline-none`}>
          {/* Main container with grid layout */}
          <div
            className={`flex-1 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none cursor-pointer`}
            onClick={() => setIsPanelOpen((open) => !open)}
          >
            <div className="text-text-color ">
              <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
            </div>
            <div className="flex-grow text-left">
              <span
                className={`block xl:text-lg font-semibold ${"text-text-color"}`}
              >
                {startDate?.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                }) || DATE_TIME_COPY.datePlaceholder}
                {endDate
                  ? " - " +
                    endDate?.toLocaleDateString("en-US", {
                      month: "short",
                      day: "2-digit",
                    })
                  : ""}
              </span>
              <span
                className={`block mt-1 text-sm ${"text-text-color"} leading-none font-light`}
              >
                {DATE_TIME_COPY.dateRange}
              </span>
            </div>

            {startDate && isPanelOpen && (
              <ClearDataButton
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setTempStartDate(null);
                  setTempEndDate(null);
                  setPartiallyAvailableStartDate(null);
                  setPartiallyAvailableEndDate(null);
                  setSelectedWeeks([]);
                  setSelectedMonth(0);
                  setActiveTab("dates");
                  setMonthStartDate(new Date());
                  setMonthEndDate(moment().add(29, "days").toDate());
                }}
              />
            )}
          </div>
        </div>

        {isPanelOpen && (
          <div className="h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 -left-0.5 right-1"></div>
        )}

        {isPanelOpen && underField && (
          <div
            ref={panelRef}
            className="absolute z-50 left-0 top-full w-full mt-2 rounded-2xl shadow-lg ring-1 ring-black/5 bg-card-color"
            tabIndex={-1}
            style={{ minWidth: 320 }}
            onScroll={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className="rounded-2xl shadow-lg ring-1 ring-black/5 bg-card-color p-8 max-h-[min(85vh,720px)] overflow-y-auto overscroll-contain">
              {panelContent}
              {saveRow}
            </div>
          </div>
        )}

        {!underField && (
          <PickerLayer
            open={isPanelOpen}
            onClose={() => setIsPanelOpen(false)}
            title="Select dates"
            wrapperRef={wrapperRef}
            desktopMode="modal"
            name="dates"
            footer={saveRow}
          >
            <div ref={panelRef} className="relative">
              {panelContent}
            </div>
          </PickerLayer>
        )}
      </div>
    </>
  );
};

export default DateRangeSelection;
